/**
 * Global JavaScript for BP Theme
 * 
 * @package bp_theme
 */

(function() {
    'use strict';
    
    /**
     * Viewport width is a functional cookie. It is stored only after that
     * category is allowed, and removed again if the visitor turns it off.
     */
    function preferencesAllowed() {
        return !!(window.bpConsent && typeof window.bpConsent.allowed === 'function' && window.bpConsent.allowed('preferences'));
    }

    function clearViewportCookie() {
        document.cookie = 'viewport_width=; Path=/; Max-Age=0; SameSite=Strict';
    }

    function setViewportCookie() {
        if (!preferencesAllowed()) {
            clearViewportCookie();
            return;
        }

        const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
        
        // Set cookie with viewport width (expires in 1 hour)
        const expires = new Date();
        expires.setTime(expires.getTime() + (60 * 60 * 1000));
        document.cookie = `viewport_width=${width}; expires=${expires.toUTCString()}; path=/; SameSite=Strict`;
    }
    
    /**
     * Update viewport cookie on resize (debounced)
     */
    let resizeTimeout;
    function handleResize() {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(setViewportCookie, 250);
    }
    
    // Set initial viewport cookie
    setViewportCookie();
    
    // Update on resize
    window.addEventListener('resize', handleResize);
    
    // Update on orientation change (mobile devices)
    window.addEventListener('orientationchange', function() {
        setTimeout(setViewportCookie, 100);
    });
    
    // Update when page becomes visible (for mobile apps)
    document.addEventListener('visibilitychange', function() {
        if (!document.hidden) {
            setViewportCookie();
        }
    });
    
})();

/**
 * Free-shipping progress on the cart block, under the totals heading.
 * The sidebar is drawn by WooCommerce in the browser, so the note is placed
 * after each redraw and the amount follows the cart store.
 */
(function () {
    'use strict';

    const cfg = window.bpCartShipping;
    if (!cfg || !(Number(cfg.goal) > 0)) {
        return;
    }

    const goal = Number(cfg.goal);
    let note = null;

    function cartData() {
        if (!window.wp || !wp.data || typeof wp.data.select !== 'function') {
            return null;
        }
        const store = wp.data.select('wc/store/cart');
        if (!store || typeof store.getCartData !== 'function') {
            return null;
        }
        const data = store.getCartData();
        if (!data || !data.totals || !data.totals.currency_code) {
            return null;
        }
        return data;
    }

    function money(totals, key) {
        const unit = Number(totals.currency_minor_unit);
        const scale = Math.pow(10, Number.isFinite(unit) ? unit : 2);
        const raw = parseInt(totals[key] || '0', 10);
        return (Number.isFinite(raw) ? raw : 0) / scale;
    }

    function formatAmount(amount, totals) {
        const whole = Math.abs(amount - Math.round(amount)) < 0.001;
        const decimals = whole ? 0 : (Number(cfg.decimals) || 2);
        const negative = amount < 0;
        const parts = Math.abs(amount).toFixed(decimals).split('.');
        const thousand = totals.currency_thousand_separator || '';
        parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousand);
        const decimal = totals.currency_decimal_separator || ',';
        const number = decimals ? parts[0] + decimal + parts[1] : parts[0];
        return (negative ? '-' : '') + (totals.currency_prefix || '') + number + (totals.currency_suffix || '');
    }

    function progressFrom(data) {
        if (!data || Number(data.itemsCount) < 1) {
            return null;
        }
        const totals = data.totals;
        const includeTax = cfg.pricesIncludeTax === true || cfg.pricesIncludeTax === 1 || cfg.pricesIncludeTax === '1';
        let total = money(totals, 'total_items');
        if (includeTax) {
            total += money(totals, 'total_items_tax');
            total -= money(totals, 'total_discount');
            total -= money(totals, 'total_discount_tax');
        } else {
            total -= money(totals, 'total_discount');
        }
        total = Math.max(0, total);
        const reached = total + 0.001 >= goal;
        const pct = reached ? 100 : Math.max(0, Math.min(100, Math.round((total / goal) * 100)));
        const text = reached
            ? String(cfg.reached || '')
            : String(cfg.left || '%s').replace('%s', formatAmount(goal - total, totals));
        return { text: text, pct: pct, reached: reached };
    }

    function ensureNote() {
        if (note) {
            return note;
        }
        note = document.createElement('div');
        note.className = 'cart-shipping-goal';
        note.innerHTML = '<p id="cart-shipping-goal-label" class="cart-shipping-goal__text"></p>'
            + '<div class="cart-shipping-goal__track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-labelledby="cart-shipping-goal-label">'
            + '<span class="cart-shipping-goal__fill"></span></div>';
        return note;
    }

    function render() {
        const root = document.querySelector('.wp-block-woocommerce-cart');
        if (!root) {
            return;
        }
        const progress = progressFrom(cartData());
        const title = root.querySelector('.wc-block-cart__totals-title');
        const heading = title ? title.closest('.wp-block-woocommerce-cart-order-summary-heading-block') || title : null;
        if (!progress || !heading || !heading.parentNode) {
            if (note && note.parentNode) {
                note.parentNode.removeChild(note);
            }
            return;
        }
        const el = ensureNote();
        if (heading.nextElementSibling !== el) {
            heading.after(el);
        }
        el.classList.toggle('is-reached', progress.reached);
        const label = el.querySelector('.cart-shipping-goal__text');
        const track = el.querySelector('.cart-shipping-goal__track');
        const fill = el.querySelector('.cart-shipping-goal__fill');
        if (label && label.textContent !== progress.text) {
            label.textContent = progress.text;
        }
        if (track) {
            track.setAttribute('aria-valuenow', String(progress.pct));
        }
        if (fill) {
            fill.style.width = progress.pct + '%';
        }
    }

    function start() {
        const root = document.querySelector('.wp-block-woocommerce-cart');
        if (!root) {
            return;
        }
        const observer = new MutationObserver(render);
        observer.observe(root, { childList: true, subtree: true });
        let tries = 0;
        const bindStore = function () {
            if (window.wp && wp.data && typeof wp.data.subscribe === 'function') {
                wp.data.subscribe(render);
                render();
                return;
            }
            if (tries < 50) {
                tries += 1;
                window.setTimeout(bindStore, 100);
            }
        };
        bindStore();
        render();
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', start);
    } else {
        start();
    }
})();
